import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_COLORS = {
  pending: "bg-amber-400",
  confirmed: "bg-emerald-400",
  completed: "bg-sky-400",
  cancelled: "bg-red-400 line-through opacity-50",
};
const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function fmt(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function BookingCalendar() {
  const [enquiries, setEnquiries] = useState([]);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(fmt(today.getFullYear(), today.getMonth(), today.getDate()));

  useEffect(() => {
    api.get("/enquiries").then((res) => setEnquiries(res.data)).catch(() => {});
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    for (const e of enquiries) {
      if (!e.preferred_date) continue;
      (map[e.preferred_date] = map[e.preferred_date] || []).push(e);
    }
    Object.values(map).forEach((list) => list.sort((a, b) => (a.preferred_time || "").localeCompare(b.preferred_time || "")));
    return map;
  }, [enquiries]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = fmt(today.getFullYear(), today.getMonth(), today.getDate());
  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const selectedBookings = byDate[selected] || [];

  const shift = (delta) => setCursor(new Date(year, month + delta, 1));

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Booking Calendar</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Requested slots at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="p-2.5 border border-[#263342] text-slate-300 hover:border-crimson hover:text-crimson transition-colors" aria-label="Previous month">
            <ChevronLeft size={16} />
          </button>
          <span className="font-display font-bold uppercase tracking-widest text-slate-100 min-w-[160px] text-center" data-testid="calendar-month-label">{monthLabel}</span>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="p-2.5 border border-[#263342] text-slate-300 hover:border-crimson hover:text-crimson transition-colors" aria-label="Next month">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-panel border border-[#263342] p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((d) => (
              <p key={d} className="text-center font-mono text-[10px] tracking-[0.2em] text-slate-500 py-2">{d}</p>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {cells.map((day, i) => {
              if (day === null) return <div key={`blank-${i}`} />;
              const dateStr = fmt(year, month, day);
              const bookings = byDate[dateStr] || [];
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selected;
              return (
                <button
                  key={dateStr}
                  data-testid={`calendar-day-${dateStr}`}
                  onClick={() => setSelected(dateStr)}
                  className={`relative min-h-[64px] sm:min-h-[76px] p-2 border text-left transition-colors ${
                    isSelected ? "border-crimson bg-crimson/10" : "border-[#263342] hover:border-slate-500"
                  } ${isToday ? "ring-1 ring-crimson/60" : ""}`}
                >
                  <span className={`font-mono text-xs ${isToday ? "text-crimson font-semibold" : "text-slate-300"}`}>{day}</span>
                  {bookings.length > 0 && (
                    <span className="absolute bottom-2 left-2 right-2 flex items-center gap-1 flex-wrap">
                      {bookings.slice(0, 3).map((b) => (
                        <span key={b.id} className={`w-2 h-2 ${STATUS_COLORS[b.status] || "bg-slate-400"}`} />
                      ))}
                      <span className="font-mono text-[10px] text-slate-400">{bookings.length}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-amber-400" /> Pending</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-emerald-400" /> Confirmed</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-sky-400" /> Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-red-400" /> Cancelled</span>
          </div>
        </div>

        <div className="bg-panel border border-[#263342] p-5" data-testid="calendar-day-panel">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crimson font-semibold">
            {new Date(`${selected}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h3 className="font-display font-bold uppercase tracking-tight text-xl text-slate-100 mt-1">
            {selectedBookings.length} {selectedBookings.length === 1 ? "Booking" : "Bookings"}
          </h3>
          <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto">
            {selectedBookings.length === 0 ? (
              <p className="font-mono text-xs text-slate-500" data-testid="calendar-day-empty">No bookings requested for this day.</p>
            ) : (
              selectedBookings.map((b, i) => (
                <div key={b.id} className="border border-[#263342] bg-[#0D1117] p-4" data-testid={`calendar-booking-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-crimson font-semibold">{b.preferred_time || "Any time"}</span>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border ${
                      b.status === "pending" ? "text-amber-400 border-amber-400/40" :
                      b.status === "confirmed" ? "text-emerald-400 border-emerald-400/40" :
                      b.status === "completed" ? "text-sky-400 border-sky-400/40" : "text-red-400 border-red-400/40"
                    }`}>{b.status}</span>
                  </div>
                  <p className="mt-2 font-semibold text-sm text-slate-100">{b.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{b.service}</p>
                  {(b.vehicle_make || b.vehicle_model) && (
                    <p className="font-mono text-[10px] text-slate-500 mt-1">{[b.vehicle_year, b.vehicle_make, b.vehicle_model].filter(Boolean).join(" ")}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
